import { Info as InfoIcon } from 'lucide-react';
import { useCallback, useRef } from 'react';
import { AsyncSelectField } from '../../shared/field/async_select_field.js';
import { SearchableSelectField } from '../../shared/field/searchable_select_field.js';
import { StaticSelectField } from '../../shared/field/static_select_field.js';
import {
  costCenterLabel,
  resolveCostCenter,
  shouldSearchCostCenters,
  type AllocationCatalog,
  type AllocationDraft,
  type AllocationOption,
} from './allocation.js';
import './checkout_fields.css';

export interface AllocationFieldCopy {
  readonly company: string;
  readonly costCenter: string;
  /** Rótulo do projeto no resumo e, com `projectRequired`, no campo (sem o sufixo de opcional). */
  readonly project: string;
  readonly projectOptional: string;
  readonly selectPlaceholder: string;
  /** A busca por nome no campo Empresa não achou nada. */
  readonly companySearchEmpty: string;
  /** A busca por nome ou código no campo Projeto não achou nada. */
  readonly projectSearchEmpty: string;
  readonly costCenterSearchPrompt: string;
  readonly costCenterSearchLoading: string;
  readonly costCenterSearchEmpty: string;
  /** Sem empresa nenhuma: não há o que alocar. */
  readonly noCorporateAccess: string;
  /** A empresa não tem centro de custo e não há busca a oferecer. */
  readonly noCostCenters: string;
  /** Com o texto, Centro de custo e Projeto ficam desabilitados, com ele no lugar do
   *  `selectPlaceholder`, enquanto não há empresa escolhida: sem ela os dois não têm lista. Ausente:
   *  seguem abertos à escolha, como sempre. */
  readonly companyFirst?: string;
}

/** Busca remota de centros de custo, escopada pela empresa. */
export type CostCenterSearch = (
  companyId: string,
  term?: string,
) => Promise<readonly AllocationOption[]>;

export interface AllocationFieldProps {
  catalog: AllocationCatalog;
  value: AllocationDraft;
  onChange: (v: AllocationDraft) => void;
  editing: boolean;
  /** Ausente: o campo fica com o catálogo embutido (preview, Storybook, testes). */
  searchCostCenters?: CostCenterSearch;
  copy: AllocationFieldCopy;
  /** Aviso do servidor sobre o centro de custo escolhido ("Saldo insuficiente…"):
   *  borda destructive no campo e a linha embaixo. Só avisa — não trava nada. */
  costCenterNotice?: string;
  /** O host exige o projeto sempre que o mostra (o checkout): o rótulo perde o
   *  «(opcional)», que mentia ali (#3841). Ausente ⇒ `copy.projectOptional`. */
  projectRequired?: boolean;
  /** Trava os três campos: nenhuma lista abre e nada muda (a alocação de um grupo que já tem
   *  corrida pedida). Só vale com `editing`. */
  disabled?: boolean;
}

type SummaryPart = { label: string; value: string };

function buildSummaryParts(
  copy: AllocationFieldCopy,
  enableProjectField: boolean | undefined,
  company: string,
  costCenter: string,
  project: string,
): SummaryPart[] {
  return [
    { label: copy.company, value: company },
    { label: copy.costCenter, value: costCenter },
    ...(enableProjectField ? [{ label: copy.project, value: project }] : []),
  ].filter((p) => p.value);
}

function AllocationSummary({ parts }: { readonly parts: SummaryPart[] }) {
  if (parts.length === 0) return null;
  return (
    <p className="cpa-summary">
      {parts.map((p, i) => (
        <span key={p.label}>
          {i > 0 && ' · '}
          <span className="cpa-summary-label">{p.label}:</span> {p.value}
        </span>
      ))}
    </p>
  );
}

/** Estado vazio do campo: o motivo pelo qual não há alocação a escolher, em vez
 *  de um `<select>` mudo. `role="alert"` como no guard do multi-viajante. */
function AllocationEmptyState({ message }: { readonly message: string }) {
  return (
    <p className="cpa-empty" role="alert">
      <InfoIcon size={14} aria-hidden />
      <span>{message}</span>
    </p>
  );
}

/** O campo de centro de custo quando o catálogo do form NÃO é exaustivo: busca
 *  no servidor a cada termo, como o `TeilSearchField` do app real.
 *
 *  Guarda as opções da última busca porque o combobox só devolve `(value, label)`
 *  e o label é o composto `code - name` — o eco do submit precisa do `name` cru. */
function CostCenterSearchField({
  copy,
  value,
  onChange,
  companyId,
  search,
  selectedLabel,
  notice,
  disabled,
}: {
  readonly disabled: boolean;
  readonly notice: string | undefined;
  readonly copy: AllocationFieldCopy;
  readonly value: AllocationDraft;
  readonly onChange: (v: AllocationDraft) => void;
  readonly companyId: string;
  readonly search: CostCenterSearch;
  readonly selectedLabel: string | undefined;
}) {
  const seen = useRef(new Map<string, AllocationOption>());
  const onSearch = useCallback(
    async (term: string) => {
      const items = await search(companyId, term);
      for (const item of items) seen.current.set(item.id, item);
      return items.map((item) => ({ value: item.id, label: costCenterLabel(item) }));
    },
    [search, companyId],
  );
  return (
    <AsyncSelectField
      label={copy.costCenter}
      message={notice}
      messageTone="destructive"
      disabled={disabled}
      value={value.costCenterId}
      {...(selectedLabel ? { selectedLabel } : {})}
      placeholder={copy.selectPlaceholder}
      promptText={copy.costCenterSearchPrompt}
      loadingText={copy.costCenterSearchLoading}
      emptyText={copy.costCenterSearchEmpty}
      onSearch={onSearch}
      onChange={(costCenterId) => {
        const picked = seen.current.get(costCenterId);
        onChange({
          ...value,
          costCenterId,
          ...(picked ? { costCenterOption: picked } : {}),
        });
      }}
    />
  );
}

function AllocationEditFields({
  copy,
  value,
  onChange,
  companyOptions,
  costCenterOptions,
  costCenterSearch,
  selectedCostCenterLabel,
  projectOptions,
  showProject,
  projectLabel,
  costCenterNotice,
  disabled = false,
}: {
  readonly disabled: boolean | undefined;
  readonly projectLabel: string;
  readonly costCenterNotice: string | undefined;
  readonly copy: AllocationFieldCopy;
  readonly value: AllocationDraft;
  readonly onChange: (v: AllocationDraft) => void;
  readonly companyOptions: { value: string; label: string }[];
  readonly costCenterOptions: { value: string; label: string }[];
  readonly costCenterSearch: CostCenterSearch | undefined;
  readonly selectedCostCenterLabel?: string;
  readonly projectOptions: { value: string; label: string }[];
  readonly showProject: boolean;
}) {
  const companyFirst = value.companyId === '' ? copy.companyFirst : undefined;
  const dependentDisabled = disabled || companyFirst !== undefined;
  const dependentPlaceholder = companyFirst ?? copy.selectPlaceholder;
  return (
    <div className="cpa-fields-frame">
      <div className="cpa-fields">
        {/* Com busca: grupos com várias empresas de nome parecido não achavam a
          certa numa lista solta (voll-intelligence-design#199). */}
        <SearchableSelectField
          label={copy.company}
          value={value.companyId}
          options={companyOptions}
          onChange={(companyId) => {
            // A busca deixa escolher de novo a empresa já escolhida: isso não é
            // troca, e não pode apagar o centro de custo e o projeto.
            if (companyId === value.companyId) return;
            // Objeto NOVO de propósito: o eco de centro de custo da empresa
            // anterior não pode sobreviver à troca.
            onChange({ companyId, costCenterId: '', projectId: '' });
          }}
          placeholder={copy.selectPlaceholder}
          emptyText={copy.companySearchEmpty}
          disabled={disabled}
        />
        {costCenterSearch ? (
          <CostCenterSearchField
            copy={copy}
            value={value}
            onChange={onChange}
            companyId={value.companyId}
            search={costCenterSearch}
            selectedLabel={selectedCostCenterLabel}
            notice={costCenterNotice}
            disabled={disabled}
          />
        ) : (
          <StaticSelectField
            label={copy.costCenter}
            message={costCenterNotice}
            messageTone="destructive"
            value={value.costCenterId}
            options={costCenterOptions}
            onChange={(costCenterId) => {
              onChange({ ...value, costCenterId });
            }}
            placeholder={dependentPlaceholder}
            disabled={dependentDisabled}
          />
        )}
        {/* Com busca, como a Empresa: clientes com dezenas de projetos
            `código-nome` rolavam a lista inteira atrás de um (#6792). */}
        {showProject && (
          <SearchableSelectField
            label={projectLabel}
            value={value.projectId}
            options={projectOptions}
            onChange={(projectId) => {
              onChange({ ...value, projectId });
            }}
            placeholder={dependentPlaceholder}
            emptyText={copy.projectSearchEmpty}
            disabled={dependentDisabled}
          />
        )}
      </div>
    </div>
  );
}

/** As listas e a escolha resolvida que os dois modos do campo consomem. Fora do
 *  componente porque juntas elas estouravam o teto de complexidade dele. */
function allocationView(
  catalog: AllocationCatalog,
  value: AllocationDraft,
  searchCostCenters: CostCenterSearch | undefined,
) {
  const selectedCompany = catalog.companies.find((c) => c.id === value.companyId);
  return {
    selectedCompany,
    companyOptions: catalog.companies.map((c) => ({ value: c.id, label: c.name })),
    costCenterOptions: (selectedCompany?.costCenters ?? []).map((cc) => ({
      value: cc.id,
      // `code - name`: o cadastro que põe a identidade no código e deixa o nome
      // vazio renderizava a opção INVISÍVEL na lista (#2459).
      label: costCenterLabel(cc),
    })),
    projectOptions: (selectedCompany?.projects ?? []).map((p) => ({ value: p.id, label: p.name })),
    selectedCostCenter: resolveCostCenter(catalog, value),
    costCenterSearch: shouldSearchCostCenters(selectedCompany, Boolean(searchCostCenters))
      ? searchCostCenters
      : undefined,
  };
}

/** Company → cost center → project picker. Project only renders when
 *  `catalog.enableProjectField` is on (client-level flag, company-scoped — Flutter
 *  parity, not `projects.length`). Rateio (multi-row split) is shape-ready at
 *  the schema level but NOT rendered here (phase 1: one allocation row). */
export function AllocationField({
  catalog,
  value,
  onChange,
  editing,
  searchCostCenters,
  copy,
  costCenterNotice,
  projectRequired,
  disabled,
}: Readonly<AllocationFieldProps>) {
  const view = allocationView(catalog, value, searchCostCenters);
  const { selectedCompany, selectedCostCenter, costCenterSearch } = view;
  const costCenterText = selectedCostCenter ? costCenterLabel(selectedCostCenter) : undefined;

  if (!editing) {
    const parts = buildSummaryParts(
      copy,
      catalog.enableProjectField,
      selectedCompany?.name ?? '',
      costCenterText ?? '',
      selectedCompany?.projects.find((p) => p.id === value.projectId)?.name ?? '',
    );
    return <AllocationSummary parts={parts} />;
  }

  // Sem nenhuma empresa não há o que alocar: renderizar o campo daria um campo
  // em branco, submit travado e zero explicação. O guard vive no próprio campo,
  // e todo host o herda (#2459).
  if (catalog.companies.length === 0) {
    return <AllocationEmptyState message={copy.noCorporateAccess} />;
  }
  // Catálogo vazio E sem busca a oferecer: dizer isso é honesto; um select mudo
  // não é. Não vale quando a busca existe — lá a lista vazia é só o estado
  // inicial do campo.
  if (selectedCompany && view.costCenterOptions.length === 0 && !costCenterSearch) {
    return <AllocationEmptyState message={copy.noCostCenters} />;
  }

  return (
    <AllocationEditFields
      copy={copy}
      value={value}
      onChange={onChange}
      companyOptions={view.companyOptions}
      costCenterOptions={view.costCenterOptions}
      costCenterSearch={costCenterSearch}
      {...(costCenterText ? { selectedCostCenterLabel: costCenterText } : {})}
      projectOptions={view.projectOptions}
      showProject={catalog.enableProjectField === true}
      projectLabel={projectRequired ? copy.project : copy.projectOptional}
      costCenterNotice={costCenterNotice}
      disabled={disabled}
    />
  );
}
